import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { handleNotificationClick } from '../../utils/notificationHelpers';
import { tournamentService } from '../../services/api';
import BadgePill from '../badges/BadgePill';
import '../../styles/notifications/NotificationItem.css';

const NotificationItem = ({ notification, onClick }) => {
  const navigate = useNavigate();
  const { markAsRead, loadNotifications } = useNotifications();
  const [responding, setResponding] = useState(false);

  const getNotificationIcon = (type) => {
    const icons = {
      team_join_request: '👥',
      team_join_request_approved: '✅',
      team_join_request_rejected: '❌',
      team_invitation: '✉️',
      team_invitation_accepted: '✅',
      team_invitation_declined: '❌',
      team_invitation_cancelled: '🚫',
      strategy_draft_approved: '📝',
      strategy_draft_rejected: '📝',
      strategy_comment_reply: '💬',
      tournament_match_starting: '🏆',
      tournament_bracket_updated: '🏆',
      tournament_participant_invitation: '🏆',
      geoguesser_lobby_invitation: '🎮',
      badge_earned: '🏅',
      general: '🔔',
    };
    return icons[type] || '🔔';
  };

  const handleMarkAsRead = async (e) => {
    e.stopPropagation(); // Prevent triggering the click handler
    if (!notification.is_read) {
      try {
        await markAsRead(notification.id);
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
  };

  const handleClick = async () => {
    if (notification.type === 'tournament_participant_invitation') return;
    if (notification.type === 'geoguesser_lobby_invitation') {
      const link = notification.formatted_link || notification.link_url;
      if (link && notification.id) {
        const sep = link.includes('?') ? '&' : '?';
        navigate(`${link}${sep}mark_notification=${notification.id}`);
      } else if (link) {
        handleNotificationClick(notification, navigate);
      }
      if (onClick) onClick();
      return;
    }
    if (notification.formatted_link || notification.link_url) {
      handleNotificationClick(notification, navigate);
    }
    if (onClick) onClick();
  };

  const tournamentPublicId = notification.link_params?.tournament_public_id;
  const participantId = notification.link_params?.participant_id;
  const invitationResponse = notification.link_params?.invitation_response;
  const isTournamentInvite = notification.type === 'tournament_participant_invitation' && (tournamentPublicId || participantId != null);
  const hasRespondedToInvite = invitationResponse === 'accepted' || invitationResponse === 'declined';
  const showInviteActions = isTournamentInvite && !hasRespondedToInvite && tournamentPublicId && participantId;

  const handleAcceptTournamentInvite = async (e) => {
    e.stopPropagation();
    if (responding || !tournamentPublicId || !participantId) return;
    setResponding(true);
    try {
      await tournamentService.acceptParticipantInvite(tournamentPublicId, participantId);
      if (window.showToast) window.showToast('Invitation accepted. You are now registered.', 'success');
      await loadNotifications({ _t: Date.now() });
      if (onClick) onClick();
      navigate(`/tournaments/${tournamentPublicId}`);
    } catch (err) {
      if (window.showToast) window.showToast(err?.response?.data?.detail || 'Failed to accept.', 'error');
    } finally {
      setResponding(false);
    }
  };

  const handleDeclineTournamentInvite = async (e) => {
    e.stopPropagation();
    if (responding || !tournamentPublicId || !participantId) return;
    setResponding(true);
    try {
      await tournamentService.declineParticipantInvite(tournamentPublicId, participantId);
      if (window.showToast) window.showToast('Invitation declined.', 'info');
      await loadNotifications({ _t: Date.now() });
      if (onClick) onClick();
    } catch (err) {
      if (window.showToast) window.showToast(err?.response?.data?.detail || 'Failed to decline.', 'error');
    } finally {
      setResponding(false);
    }
  };

  const isTeamJoinRequest = notification.type === 'team_join_request';

  return (
    <div
      className={`notification-item ${notification.is_read ? 'read' : 'unread'} ${isTeamJoinRequest ? 'notification-item--join-request' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-item-icon">
        {notification.type === 'badge_earned' && notification.badge ? (
          <BadgePill
            slug={notification.badge.slug}
            name={notification.badge.name}
            tier={notification.badge.tier}
            icon={notification.badge.icon}
            style={notification.badge.style}
            style_config={notification.badge.style_config}
            description={notification.badge.description}
            size="small"
          />
        ) : (
          getNotificationIcon(notification.type)
        )}
      </div>
      <div className="notification-item-content">
        <div className="notification-item-header">
          <h4 className="notification-item-title">{notification.title}</h4>
          <div className="notification-item-header-right">
            {notification.time_ago && (
              <span className="notification-item-time">{notification.time_ago}</span>
            )}
            {!notification.is_read && !isTournamentInvite && notification.type !== 'geoguesser_lobby_invitation' && (
              <button
                className="notification-mark-read-btn"
                onClick={handleMarkAsRead}
                title="Mark as read"
              >
                Mark as read
              </button>
            )}
          </div>
        </div>
        {isTeamJoinRequest ? (
          <div className="notification-join-request-body">
            <p className="notification-join-request-message">{notification.message}</p>
            <span className="notification-join-request-cta">Review request →</span>
          </div>
        ) : (
          <p className="notification-item-message">{notification.message}</p>
        )}
        {hasRespondedToInvite && (
          <p className="notification-item-response">
            You {invitationResponse === 'accepted' ? 'accepted' : 'declined'}.
          </p>
        )}
        {showInviteActions && (
          <div className="notification-item-actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="notification-action-btn notification-action-accept"
              onClick={handleAcceptTournamentInvite}
              disabled={responding}
            >
              {responding ? '…' : 'Accept'}
            </button>
            <button
              type="button"
              className="notification-action-btn notification-action-decline"
              onClick={handleDeclineTournamentInvite}
              disabled={responding}
            >
              Decline
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
